<template>
  <div class="postal-code-zone-root">
    <!-- 顶部标题栏 -->
    <div class="header-bar">
      <span class="header-title">邮编分区管理</span>
    </div>

    <!-- 主体布局：左侧邮编列表，右侧分组管理 -->
    <div class="main-row">
      <!-- 左侧：分组管理 -->
      <div class="left-panel" :style="{width: leftWidth + 'px'}">
        <div class="group-card">
          <!-- 搜索栏和操作按钮 -->
          <div class="search-bar-row">
            <a-input
              placeholder="搜索分组"
              style="width: 60%;"
              v-model="groupQueryParam.groupName"
              allowClear
              @pressEnter="handleGroupSearch"
              @change="handleGroupInputChange"
            >
              <a-icon slot="prefix" type="search"/>
              <a-icon 
                slot="suffix" 
                type="search" 
                class="search-icon"
                @click="handleGroupSearch"
              />
            </a-input>
            <div class="search-bar-actions">
              <a-button type="primary" @click="handleAddGroup">
                <a-icon type="plus"/>
                新增分组
              </a-button>
              <a-tooltip placement="top" title="刷新">
                <a-icon type="reload" class="search-bar-icon" @click="loadGroupData"/>
              </a-tooltip>
            </div>
          </div>

          <!-- 分组卡片列表 -->
          <div class="group-card-list">
            <a-spin :spinning="groupLoading">
              <div class="group-cards-container">
                <div 
                  v-for="group in groupData" 
                  :key="group.id"
                  class="group-card-item"
                  :class="{ 'selected': selectedGroupId === group.id }"
                  @click="handleGroupSelect(group)"
                >
                  <div class="group-card-header">
                    <div class="group-name">
                      {{ group.groupName }}
                      <a-tooltip placement="top" title="复制分组名称">
                        <a-icon 
                          type="copy" 
                          class="copy-icon group-copy-icon" 
                          @click.stop="copyToClipboard(group.groupName)" 
                        />
                      </a-tooltip>
                    </div>
                    <a-dropdown :trigger="['click']">
                      <a-icon type="more" class="group-more-icon" @click.stop />
                      <a-menu slot="overlay">
                        <a-menu-item @click="handleEditGroup(group)">
                          <a-icon type="edit" />
                          编辑
                        </a-menu-item>
                      </a-menu>
                    </a-dropdown>
                  </div>
                  <div class="group-card-content">
                    <div class="group-info">
                      <span class="postal-count">邮编数量: {{ group.postalCount || 0 }} 个</span>
                    </div>
                    <div class="group-status">
                      <a-tag :color="group.status === 1 ? 'green' : 'red'">
                        {{ group.status === 1 ? '启用' : '禁用' }}
                      </a-tag>
                    </div>
                  </div>
                </div>
                
                <!-- 空状态 -->
                <div v-if="!groupLoading && groupData.length === 0" class="empty-state">
                  <a-empty description="暂无分组数据" />
                </div>
              </div>
            </a-spin>

            <!-- 分组分页 -->
            <div class="menu-pagination">
              <a-pagination
                v-model="groupPagination.current"
                :total="groupPagination.total"
                :pageSize="groupPagination.pageSize"
                :showSizeChanger="true"
                :showTotal="(total) => `总共 ${total} 个分组`"
                size="small"
                @change="handleGroupPageChange"
                @showSizeChange="handleGroupPageChange"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 拖拽分割条 -->
      <div class="drag-bar" @mousedown="onDragStart"></div>

      <!-- 右侧：邮编详情（需选择左侧分组） -->
      <div class="right-panel">
        <div class="main-card">
          <!-- 顶部信息 -->
          <div class="main-info">
            <div class="main-info-header">
              <div class="main-info-title">
                {{ selectedGroupName || '邮编列表' }}
                <a-tooltip placement="top" title="刷新">
                  <a-icon type="reload" class="refresh-icon" @click="loadPostalData" />
                </a-tooltip>
              </div>
              <div class="main-info-actions">
                <a-button
                  type="primary"
                  :disabled="!selectedGroupId"
                  @click="handleAddPostal"
                >
                  <a-icon type="plus"/>
                  新增邮编
                </a-button>
              </div>
            </div>
            <div class="main-info-desc">
              {{ selectedGroupName ? `分组：${selectedGroupName} 的邮编信息` : '请在左侧选择分组以查看邮编' }}
            </div>
          </div>

          <!-- 邮编搜索 -->
          <div class="detail-search" v-if="selectedGroupId">
            <a-form layout="inline">
              <a-form-item>
                <a-select
                  placeholder="邮编类型"
                  v-model="postalQueryParam.postalCodeType"
                  allowClear
                  style="width: 120px"
                >
                  <a-select-option :value="1">
                    <a-icon type="number"/>
                    绝对值
                  </a-select-option>
                  <a-select-option :value="2">
                    <a-icon type="swap"/>
                    区间
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item>
                <a-input
                  placeholder="搜索邮编"
                  v-model="postalQueryParam.zipCode"
                  allowClear
                  style="width: 150px"
                >
                  <a-icon slot="prefix" type="search"/>
                </a-input>
              </a-form-item>
              <a-form-item>
                <a-button type="primary" @click="handlePostalSearch">
                  <a-icon type="search"/>
                  搜索
                </a-button>
                <a-button @click="resetPostalSearch" style="margin-left: 8px">
                  <a-icon type="reload"/>
                  重置
                </a-button>
              </a-form-item>
            </a-form>
          </div>

          <!-- 邮编详情表格 -->
          <div class="detail-table" v-if="selectedGroupId">
            <a-table
              size="middle"
              rowKey="id"
              :columns="postalColumns"
              :dataSource="postalDataSource"
              :pagination="false"
              :loading="postalLoading"
              :scroll="{ x: 1000 }"
              class="modern-table main-table"
            >
              <!-- 邮编类型列 -->
              <template slot="postalCodeType" slot-scope="text">
                <a-tag :color="text === 1 ? 'green' : 'orange'">
                  <a-icon :type="text === 1 ? 'number' : 'swap'"/>
                  {{ text === 1 ? '绝对值' : '区间' }}
                </a-tag>
              </template>

              <!-- 邮编列 -->
              <template slot="zipCode" slot-scope="text, record">
                <div class="zipcode-content">
                  <template v-if="record.postalCodeType === 1">
                    <a-tag color="blue">
                      {{ record.zipCode || '-' }}
                    </a-tag>
                    <a-tooltip title="复制邮编">
                      <a-icon 
                        type="copy" 
                        class="copy-icon" 
                        @click="copyToClipboard(record.zipCode)"
                      />
                    </a-tooltip>
                  </template>
                  <template v-else-if="record.postalCodeType === 2">
                    <div class="range-display">
                      <a-tag color="cyan">{{ record.zipCodeStart || '-' }}</a-tag>
                      <a-icon type="arrow-right" class="range-arrow"/>
                      <a-tag color="cyan">{{ record.zipCodeEnd || '-' }}</a-tag>
                      <a-tooltip title="复制邮编区间">
                        <a-icon 
                          type="copy" 
                          class="copy-icon" 
                          @click="copyToClipboard(`${record.zipCodeStart}-${record.zipCodeEnd}`)"
                        />
                      </a-tooltip>
                    </div>
                  </template>
                  <span v-else>-</span>
                </div>
              </template>

              <!-- 备注列 -->
              <template slot="remarks" slot-scope="text">
                <a-tooltip :title="text || '-'" placement="top">
                  <div class="remarks-cell">
                    {{ text || '-' }}
                  </div>
                </a-tooltip>
              </template>

              <!-- 操作列 -->
              <template slot="action" slot-scope="text, record">
                <a-button type="link" size="small" @click="handleEditPostal(record)">编辑</a-button>
              </template>
            </a-table>

            <!-- 分页组件 -->
            <div class="table-pagination">
              <a-pagination
                v-model="postalPagination.current"
                :total="postalPagination.total"
                :page-size="postalPagination.pageSize"
                :show-size-changer="true"
                :show-quick-jumper="true"
                :show-total="(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`"
                @change="handlePostalPageChange"
                @showSizeChange="handlePostalPageSizeChange"
                size="小"
              />
            </div>
          </div>

          <!-- 未选择分组时的提示 -->
          <div v-if="!selectedGroupId" class="no-selection">
            <a-empty description="请先在左侧选择分组"/>
          </div>
        </div>
      </div>
    </div>

    <!-- 邮编新增/编辑模态框 -->
    <a-modal
      :title="postalModalTitle"
      :visible="postalModalVisible"
      @ok="handlePostalOk"
      @cancel="handlePostalCancel"
      :confirmLoading="postalConfirmLoading"
      :destroyOnClose="true"
      width="680px"
      class="现代-modal"
      :maskClosable="false"
    >
      <template slot="title">
        <div class="modal-title">
          <a-icon :type="editingPostal ? 'edit' : 'plus'" class="modal-icon"/>
          {{ postalModalTitle }}
        </div>
      </template>

      <div class="modal-content">
        <a-form :form="postalForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-form-item label="所属分组">
            <a-input :value="selectedGroupName" disabled />
          </a-form-item>

          <a-form-item label="邮编类型">
            <a-select
              v-decorator="[
                'postalCodeType',
                { rules: [{ required: true, message: '请选择邮编类型!' }] }
              ]"
              @change="onPostalCodeTypeChange"
              placeholder="请选择邮编类型"
              class="form-select"
            >
              <a-select-option :value="1">
                <a-icon type="number"/>
                邮编绝对值
              </a-select-option>
              <a-select-option :value="2">
                <a-icon type="swap"/>
                邮编区间
              </a-select-option>
            </a-select>
          </a-form-item>

          <!-- 邮编绝对值输入 -->
          <a-form-item label="邮编" v-if="postalCodeType === 1">
            <a-input
              v-decorator="[
                'zipCode',
                {
                  rules: [
                    { required: postalCodeType === 1, message: '请输入邮编!' },
                    { pattern: /^\d{1,10}$/, message: '请输入10位以内数字邮编!' }
                  ]
                }
              ]"
              placeholder="请输入邮编"
              class="form-input"
              :maxLength="10"
            >
              <a-icon slot="prefix" type="number"/>
            </a-input>
          </a-form-item>

          <!-- 邮编区间输入 -->
          <template v-if="postalCodeType === 2">
            <a-form-item label="邮编起始值">
              <a-input
                v-decorator="[
                  'zipCodeStart',
                  {
                    rules: [
                      { required: postalCodeType === 2, message: '请输入邮编起始值!' },
                      { pattern: /^\d{1,10}$/, message: '请输入10位以内数字邮编!' }
                    ]
                  }
                ]"
                placeholder="请输入起始邮编"
                class="form-input"
                :maxLength="10"
              >
                <a-icon slot="prefix" type="arrow-right"/>
              </a-input>
            </a-form-item>

            <a-form-item label="邮编结束值">
              <a-input
                v-decorator="[
                  'zipCodeEnd',
                  {
                    rules: [
                      { required: postalCodeType === 2, message: '请输入邮编结束值!' },
                      { pattern: /^\d{1,10}$/, message: '请输入10位以内数字邮编!' }
                    ]
                  }
                ]"
                placeholder="请输入结束邮编"
                class="form-input"
                :maxLength="10"
              >
                <a-icon slot="prefix" type="arrow-left"/>
              </a-input>
            </a-form-item>

            <!-- 区间提示 -->
            <a-form-item :wrapper-col="{ span: 16, offset: 6 }">
              <a-alert
                message="提示：起始值必须小于结束值"
                type="info"
                show-icon
                class="range-tip"
              />
            </a-form-item>
          </template>

          <a-form-item label="备注">
            <a-textarea
              v-decorator="['remarks']"
              placeholder="请输入备注信息（可选）"
              :rows="3"
              class="form-textarea"
              :maxLength="200"
              show-count
            />
          </a-form-item>
        </a-form>
      </div>

      <template slot="footer">
        <div class="modal-footer">
          <a-button @click="handlePostalCancel" class="cancel-btn">取消</a-button>
          <a-button
            type="primary"
            @click="handlePostalOk"
            :loading="postalConfirmLoading"
            class="confirm-btn"
          >
            {{ editingPostal ? '保存' : '新增' }}
          </a-button>
        </div>
      </template>
    </a-modal>

    <!-- 分组新增/编辑模态框 -->
    <a-modal
      :title="groupModalTitle"
      :visible="groupModalVisible"
      @ok="handleGroupOk"
      @cancel="handleGroupCancel"
      :confirmLoading="groupConfirmLoading"
      :destroyOnClose="true"
      width="520px"
      class="modern-modal"
      :maskClosable="false"
    >
      <template slot="title">
        <div class="modal-title">
          <a-icon :type="editingGroup ? 'edit' : 'plus'" class="modal-icon"/>
          {{ groupModalTitle }}
        </div>
      </template>

      <div class="modal-content">
        <a-form :form="groupForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
          <a-form-item label="分组名称">
            <a-input
              v-decorator="[
                'groupName',
                { rules: [{ required: true, message: '请输入分组名称!' }] }
              ]"
              placeholder="请输入分组名称"
            >
              <a-icon slot="prefix" type="environment"/>
            </a-input>
          </a-form-item>

          <a-form-item label="状态">
            <a-select
              v-decorator="[
                'status',
                { rules: [{ required: true, message: '请选择状态!' }] }
              ]"
              placeholder="请选择状态"
            >
              <a-select-option :value="1">启用</a-select-option>
              <a-select-option :value="2">禁用</a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </div>

      <template slot="footer">
        <div class="modal-footer">
          <a-button @click="handleGroupCancel" class="cancel-btn">取消</a-button>
          <a-button
            type="primary"
            @click="handleGroupOk"
            :loading="groupConfirmLoading"
            class="confirm-btn"
          >
            {{ editingGroup ? '保存' : '新增' }}
          </a-button>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { TableListMixin } from '@/situation/mixins/tableListMixin'
import { pagePostalCodeGroup, createPostalCodeGroup, updatePostalCodeGroup } from '@/api/postalCodeGroup'
import { pagePostalCode, createPostalCode, updatePostalCode } from '@/api/postalCode'

export default {
  name: 'PostalCodeZone',
  mixins: [TableListMixin],
  mounted() {
    this.loadGroupData()
  },
  data() {
    return {
      description: '邮编分区管理页面',

      // 布局相关
      leftWidth: 560,
      isDragging: false,
      dragStartX: 0,
      initialLeftWidth: 560,

      // 选中的分组
      selectedGroupId: null,
      selectedGroupName: '',

      // 分组相关（右侧）
      groupData: [],
      groupLoading: false,
      groupQueryParam: { groupName: '' },
      groupPagination: { current: 1, pageSize: 10, total: 0 },
      groupColumns: [
        { title: '分组名称', dataIndex: 'groupName', align: 'center', width: 200 },
        { title: '状态', dataIndex: 'status', align: 'center', width: 120, scopedSlots: { customRender: 'status' } },
        { title: '操作', dataIndex: 'action', align: 'center', width: 120, scopedSlots: { customRender: 'action' } }
      ],

      // 邮编详情（左侧）
      postalDataSource: [],
      postalLoading: false,
      postalQueryParam: { postalCodeType: undefined, zipCode: '' },
      postalPagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showQuickJumper: true },
      postalColumns: [
        { title: '邮编类型', align: 'center', width: 100, dataIndex: 'postalCodeType', scopedSlots: { customRender: 'postalCodeType' } },
        { title: '邮编', align: 'center', width: 200, dataIndex: 'zipCode', scopedSlots: { customRender: 'zipCode' } },
        { title: '备注', align: 'center', width: 180, dataIndex: 'remarks', scopedSlots: { customRender: 'remarks' } },
        { title: '创建人', align: 'center', width: 120, dataIndex: 'creator' },
        { title: '创建时间', align: 'center', width: 160, dataIndex: 'createTime' },
        { title: '操作', dataIndex: 'action', align: 'center', fixed: 'right', width: 120, scopedSlots: { customRender: 'action' } }
      ],

      // 邮编弹窗
      postalModalVisible: false,
      postalModalTitle: '',
      postalConfirmLoading: false,
      postalForm: this.$form.createForm(this),
      editingPostal: null,
      postalCodeType: 1,

      // 分组弹窗
      groupModalVisible: false,
      groupModalTitle: '',
      groupConfirmLoading: false,
      groupForm: this.$form.createForm(this),
      editingGroup: null,
    }
  },
  methods: {
    // 复制到剪贴板
    copyToClipboard(text) {
      if (!text) {
        this.$message.warning('没有可复制的内容')
        return
      }
      
      // 使用现代浏览器的 Clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success(`已复制: ${text}`)
        }).catch(err => {
          console.error('复制失败:', err)
          this.fallbackCopyTextToClipboard(text)
        })
      } else {
        // 降级方案
        this.fallbackCopyTextToClipboard(text)
      }
    },

    // 降级复制方案
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      
      try {
        const successful = document.execCommand('copy')
        if (successful) {
          this.$message.success(`已复制: ${text}`)
        } else {
          this.$message.error('复制失败')
        }
      } catch (err) {
        console.error('复制失败:', err)
        this.$message.error('复制失败')
      }
      
      document.body.removeChild(textArea)
    },

    // 分组数据加载（右侧）
    loadGroupData() {
      this.groupLoading = true
      const params = {
        current: this.groupPagination.current,
        size: this.groupPagination.pageSize,
        groupName: this.groupQueryParam.groupName || undefined
      }
      pagePostalCodeGroup(params).then(res => {
        if (res.code === 200) {
          const records = res.data.records || res.data || []
          this.groupData = records
          this.groupPagination.total = res.data.total || records.length || 0
        } else {
          this.$message.error(res.msg || '加载分组数据失败')
        }
      }).catch(err => {
        console.error('加载分组数据失败:', err)
        this.$message.error('加载分组数据失败')
      }).finally(() => {
        this.groupLoading = false
      })
    },

    // 分组选中，加载右侧邮编
    handleGroupSelect(group) {
      this.selectedGroupId = group.id
      this.selectedGroupName = group.groupName
      this.postalPagination.current = 1
      this.loadPostalData()
    },
    groupCustomRow(record) {
      return {
        on: {
          click: () => this.handleGroupSelect(record)
        }
      }
    },

    // 分组搜索与分页
    handleGroupSearch() {
      this.groupPagination.current = 1
      this.loadGroupData()
    },
    
    // 处理分组搜索框输入变化（包括清除操作）
    handleGroupInputChange(e) {
      // 当输入框被清空时（点击清除按钮或手动删除所有内容）
      if (!e.target.value) {
        this.groupPagination.current = 1
        this.loadGroupData()
      }
    },
    
    handleGroupPageChange(page, pageSize) {
      this.groupPagination.current = page
      this.groupPagination.pageSize = pageSize
      this.loadGroupData()
    },

    // 邮编数据加载（左侧）
    loadPostalData() {
      if (!this.selectedGroupId) {
        this.postalDataSource = []
        this.postalPagination.total = 0
        return
      }
      this.postalLoading = true
      const params = {
        groupId: this.selectedGroupId,
        current: this.postalPagination.current,
        size: this.postalPagination.pageSize,
        zipCode: this.postalQueryParam.zipCode || undefined,
        postalCodeType: this.postalQueryParam.postalCodeType || undefined
      }
      pagePostalCode(params).then(res => {
        if (res.code === 200) {
          const records = res.data.records || res.data || []
          this.postalDataSource = records
          this.postalPagination.total = res.data.total || records.length || 0
        } else {
          this.$message.error(res.msg || '加载邮编数据失败')
        }
      }).catch(err => {
        console.error('加载邮编数据失败:', err)
        this.$message.error('加载邮编数据失败')
      }).finally(() => {
        this.postalLoading = false
      })
    },

    // 邮编搜索与分页
    handlePostalSearch() {
      this.postalPagination.current = 1
      this.loadPostalData()
    },
    resetPostalSearch() {
      this.postalQueryParam = { postalCodeType: undefined, zipCode: '' }
      this.postalPagination.current = 1
      this.loadPostalData()
    },
    handlePostalPageChange(page, pageSize) {
      this.postalPagination.current = page
      this.postalPagination.pageSize = pageSize
      this.loadPostalData()
    },
    handlePostalPageSizeChange(current, size) {
      this.postalPagination.current = 1
      this.postalPagination.pageSize = size
      this.loadPostalData()
    },

    // 邮编弹窗
    handleAddPostal() {
      if (!this.selectedGroupId) {
        this.$message.warning('请先选择分组后再新增邮编')
        return
      }
      this.postalModalTitle = '新增邮编'
      this.postalModalVisible = true
      this.editingPostal = null
      this.postalCodeType = 1
      this.$nextTick(() => {
        this.postalForm.resetFields()
        this.postalForm.setFieldsValue({ postalCodeType: 1 })
      })
    },
    handleEditPostal(record) {
      this.postalModalTitle = '编辑邮编'
      this.postalModalVisible = true
      this.editingPostal = record
      this.postalCodeType = record.postalCodeType || 1
      this.$nextTick(() => {
        this.postalForm.resetFields()
        this.postalForm.setFieldsValue({
          postalCodeType: record.postalCodeType,
          zipCode: record.zipCode,
          zipCodeStart: record.zipCodeStart,
          zipCodeEnd: record.zipCodeEnd,
          remarks: record.remarks
        })
      })
    },
    onPostalCodeTypeChange(value) {
      this.postalCodeType = value
    },
    handlePostalOk() {
      if (!this.selectedGroupId) {
        this.$message.error('未选择分组，无法提交邮编')
        return
      }
      this.postalConfirmLoading = true
      this.postalForm.validateFields((err, values) => {
        if (err) {
          this.postalConfirmLoading = false
          return
        }

        const payload = {
          groupId: this.selectedGroupId,
          postalCodeType: values.postalCodeType,
          remarks: values.remarks || null
        }

        if (values.postalCodeType === 1) {
          payload.zipCode = values.zipCode
          payload.zipCodeStart = null
          payload.zipCodeEnd = null
        } else {
          payload.zipCode = null
          payload.zipCodeStart = values.zipCodeStart
          payload.zipCodeEnd = values.zipCodeEnd
          if (parseInt(values.zipCodeStart) >= parseInt(values.zipCodeEnd)) {
            this.$message.error('邮编起始值必须小于结束值!')
            this.postalConfirmLoading = false
            return
          }
        }

        const apiCall = this.editingPostal
          ? updatePostalCode({ ...payload, id: this.editingPostal.id })
          : createPostalCode(payload)

        apiCall.then(res => {
          if (res.code === 200) {
            this.$message.success(this.editingPostal ? '更新成功!' : '新增成功!')
            this.postalModalVisible = false
            this.loadPostalData()
          } else {
            this.$message.error(res.msg || '操作失败!')
          }
        }).catch(error => {
          this.$message.error('操作失败!')
          console.error(error)
        }).finally(() => {
          this.postalConfirmLoading = false
        })
      })
    },
    handlePostalCancel() {
      this.postalModalVisible = false
    },

    // 分组弹窗
    handleAddGroup() {
      this.groupModalTitle = '新增分组'
      this.groupModalVisible = true
      this.editingGroup = null
      this.$nextTick(() => {
        this.groupForm.resetFields()
      })
    },
    handleEditGroup(record) {
      this.groupModalTitle = '编辑分组'
      this.groupModalVisible = true
      this.editingGroup = record
      this.$nextTick(() => {
        this.groupForm.resetFields()
        this.groupForm.setFieldsValue({
          groupName: record.groupName,
          status: record.status
        })
      })
    },
    handleGroupOk() {
      this.groupConfirmLoading = true
      this.groupForm.validateFields((err, values) => {
        if (err) {
          this.groupConfirmLoading = false
          return
        }
        const payload = { groupName: values.groupName, status: values.status }
        const apiCall = this.editingGroup
          ? updatePostalCodeGroup({ ...payload, id: this.editingGroup.id })
          : createPostalCodeGroup(payload)
        apiCall.then(res => {
          if (res.code === 200) {
            this.$message.success(this.editingGroup ? '更新成功!' : '新增成功!')
            this.groupModalVisible = false
            this.loadGroupData()
          } else {
            this.$message.error(res.msg || '操作失败!')
          }
        }).catch(error => {
          this.$message.error('操作失败!')
          console.error(error)
        }).finally(() => {
          this.groupConfirmLoading = false
        })
      })
    },
    handleGroupCancel() {
      this.groupModalVisible = false
    },

    // 拖拽分隔条
    onDragStart(e) {
      this.isDragging = true
      this.dragStartX = e.clientX
      this.initialLeftWidth = this.leftWidth
      window.addEventListener('mousemove', this.onDragMove)
      window.addEventListener('mouseup', this.onDragEnd)
    },
    onDragMove(e) {
      if (!this.isDragging) return
      const delta = e.clientX - this.dragStartX
      const targetWidth = this.initialLeftWidth + delta
      const min = 380
      const max = 900
      this.leftWidth = Math.max(min, Math.min(max, targetWidth))
    },
    onDragEnd() {
      this.isDragging = false
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mouseup', this.onDragEnd)
    }
  }
}
</script>

<style scoped>
.postal-code-zone-root {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}
.header-title {
  font-size: 16px;
  font-weight: 600;
}
.main-row {
  display: flex;
  height: calc(100% - 54px);
}
.left-panel {
  border-right: 1px solid #f0f0f0;
  background: #fff;
}
.right-panel {
  flex: 1;
  background: #fff;
}
.drag-bar {
  width: 6px;
  cursor: col-resize;
  background: #fafafa;
  border-left: 1px solid #f0f0f0;
  border-right: 1px solid #f0f0f0;
}
.main-card, .group-card {
  padding: 16px;
  height: calc(100vh - 180px);
  display: flex;
  flex-direction: column;
}
.main-info-header { display: flex; align-items: center; justify-content: space-between; }
.main-info-title {
  font-size: 14px;
  font-weight: 600;
}
.main-info-actions { display: inline-flex; align-items: center; }
.main-info-actions .ant-btn { margin-left: 8px; }
.refresh-icon {
  margin-left: 6px;
  cursor: pointer;
}
.detail-search { margin-top: 12px; flex-shrink: 0; }
.detail-table { 
  margin-top: 12px; 
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.detail-table .ant-table-wrapper {
  flex: 1;
  overflow: hidden;
}
.detail-table .ant-table-tbody {
  overflow-y: auto;
  max-height: calc(100% - 55px);
}
.table-pagination { margin-top: 12px; display: flex; justify-content: flex-end; flex-shrink: 0; }
.search-icon {
  cursor: pointer;
  color: #8c8c8c;
  transition: color 0.2s;
}

.search-icon:hover {
  color: #1890ff;
}

.group-copy-icon {
  margin-left: 6px;
  opacity: 1;
  transition: all 0.2s;
  font-size: 12px;
  color: #52c41a;
}

.group-copy-icon:hover {
  color: #389e0d;
  transform: scale(1.1);
}

.copy-icon {
  margin-left: 8px;
  cursor: pointer;
  color: #8c8c8c;
  transition: all 0.2s;
  font-size: 14px;
}

.copy-icon:hover {
  color: #1890ff;
  transform: scale(1.1);
}

.zipcode-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.range-display {
  display: inline-flex;
  align-items: center;
}

.search-bar-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; flex-shrink: 0; }
.search-bar-actions { display: inline-flex; align-items: center; }
.search-bar-actions .ant-btn { margin-left: 8px; }
.search-bar-icon { cursor: pointer; margin-left: 8px; }
.group-card-list {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.group-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.group-card-item {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.group-card-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
  transform: translateY(-1px);
}

.group-card-item.selected {
  border-color: #1890ff;
  background: #f6ffed;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
}

.group-card-item.selected::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #1890ff;
  border-radius: 4px 0 0 4px;
}

.group-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.group-name {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  flex: 1;
}

.group-more-icon {
  color: #8c8c8c;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.group-more-icon:hover {
  color: #1890ff;
  background: #f0f0f0;
}

.group-card-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.group-info {
  flex: 1;
}

.postal-count {
  color: #595959;
  font-size: 14px;
}

.group-status {
  flex-shrink: 0;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.group-table {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.group-table .ant-table-wrapper {
  flex: 1;
  overflow: hidden;
}
.group-table .ant-table-tbody {
  overflow-y: auto;
  max-height: calc(100% - 55px);
}
.menu-pagination { 
  margin-top: 12px; 
  display: flex; 
  justify-content: flex-end; 
  flex-shrink: 0; 
}
.remarks-cell { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; }
.zipcode-content { display: flex; align-items: center; justify-content: center; }
.range-display { display: inline-flex; align-items: center; }
.range-arrow { margin: 0 6px; }
.no-selection { padding: 40px 0; }
.modal-title { display: flex; align-items: center; }
.modal-icon { margin-right: 8px; }
.modal-footer { display: flex; justify-content: flex-end; }
</style>
